import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];
  #scrollHandler = null;
  /** @type {((event: FocusEvent) => void) | null} */
  #inputFocusHandler = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupScrollListener();
    this.#setupInputFocusListener();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#removeScrollListener();
    this.#removeInputFocusListener();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.classList.contains('menu-open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    if (event) {
      event.preventDefault();
    }
    return this.isOpen ? this.close(event) : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    if (event) {
      event.preventDefault();
    }
    
    this.#updateDrawerPosition();

    const details = this.#getDetailsElement(event);
    
    details.setAttribute('open', ''); 

    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    if (event) {
      event.preventDefault();
    }
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   * @param {Event} [event]
   */
  close(event) {
    if (event) {
      event.preventDefault();
    }
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Setup scroll listener to dynamically position the drawer
   */
  #setupScrollListener() {
    this.#updateDrawerPosition();
    this.#scrollHandler = this.#updateDrawerPosition.bind(this);
    
    if (this.#scrollHandler) {
      window.addEventListener('scroll', this.#scrollHandler, { passive: true });
      window.addEventListener('resize', this.#scrollHandler, { passive: true });
    }
  }

  /**
   * Remove scroll listener
   */
  #removeScrollListener() {
    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
      window.removeEventListener('resize', this.#scrollHandler);
      this.#scrollHandler = null;
    }
  }

  /**
   * Update drawer position based on header section position
   */
  #updateDrawerPosition() {
    const headerSection = document.querySelector('.header-section');
    if (!headerSection) return;

    const headerBottom = headerSection.getBoundingClientRect().bottom;
    
    this.style.setProperty('--drawer-top', `${headerBottom}px`);
    this.style.setProperty('--drawer-height', `calc(100dvh - ${headerBottom}px)`);
  }

  /**
   * Temporarily disables accordion animations
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * Setup input focus listener to handle opacity on focus
   */
  #setupInputFocusListener() {
    /** @param {FocusEvent} event */
    const handler = (event) => {
      const target = event.target;
      
      // Only handle inputs, selects, and textareas within the drawer
      if (!(target instanceof HTMLInputElement || 
            target instanceof HTMLSelectElement || 
            target instanceof HTMLTextAreaElement)) {
        return;
      }

      // Only handle if the element is within this drawer
      if (!this.contains(target)) {
        return;
      }

      // Set opacity to 0 on focus, then back to 1
      // Use requestAnimationFrame to ensure it doesn't interfere with focus
      if (target instanceof HTMLElement) {
        requestAnimationFrame(() => {
          target.style.opacity = '0';
          requestAnimationFrame(() => {
            target.style.opacity = '1';
          });
        });
      }
    };

    this.#inputFocusHandler = handler;
    // Use focusin instead of focus - focusin bubbles, focus doesn't
    // Don't use capture phase to avoid interfering with focus trap
    this.addEventListener('focusin', handler, false);
  }

  /**
   * Remove input focus listener
   */
  #removeInputFocusListener() {
    if (this.#inputFocusHandler !== null) {
      this.removeEventListener('focusin', this.#inputFocusHandler, false);
      this.#inputFocusHandler = null;
    }
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
